/** Platform features behind one interface with a web fallback (CLAUDE.md). Native shells
 * (Capacitor, Tauri) replace entries here; the web build must work with every entry absent.
 * Capacitor plugins are addressed by name through the runtime bridge, so the web bundle needs
 * no plugin packages: when the native side is missing, the call is not offered at all. */
import { Capacitor, SystemBars, SystemBarsStyle, registerPlugin } from "@capacitor/core";

import { product } from "../product";
import { cardCameraFor, coarsePointer, type CardCamera } from "./cardCamera";
import { nsKey } from "./storage";

export type Platform = "web" | "android" | "ios";
export const platform: Platform = (() => {
  try {
    const p = Capacitor.getPlatform();
    return p === "android" || p === "ios" ? p : "web";
  } catch {
    return "web";
  }
})();
const native = platform !== "web";
const hasPlugin = (name: string): boolean => {
  try {
    return native && Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
};

/** Which native shell bundles us. Both shells ship the same web build; the Capacitor config
 * (apps/mobile/scripts/gen_config.py, `appendUserAgent`) marks the user agent with
 * `<slug>-shell/student|teacher`, which is the source of truth. Plugin presence is the
 * fallback for shells built before the marker existed (only the student shell carries the
 * barcode scanner and focus mode). Null on the web and in the desktop shell. */
export type Shell = "student" | "teacher" | null;
function shellFromUserAgent(): Exclude<Shell, null> | undefined {
  if (typeof navigator === "undefined") return undefined;
  const ua = navigator.userAgent;
  const marker = `${product.slug}-shell/`; // includes(): the slug is never treated as a pattern
  if (ua.includes(`${marker}student`)) return "student";
  if (ua.includes(`${marker}teacher`)) return "teacher";
  return undefined;
}
export const shell: Shell =
  shellFromUserAgent() ?? (native ? (hasPlugin("CapacitorBarcodeScanner") || hasPlugin("FocusMode") ? "student" : "teacher") : null);

/** System bars (status and navigation) on the native shells. Icon contrast and visibility
 * come from Capacitor core's SystemBars plugin; the bar's own colour goes through
 * `@capacitor/status-bar` when the shell ships it (adopted 2026-09-08; it only paints on
 * Android 8-14 and iOS, on Android 15+ the page paints the strip under the bar, see
 * apps/web/src/platform/surface.ts). Addressed by name so the web bundle stays free of the
 * package; null on the web, where the theme-color meta is the fallback.
 *
 * When the package is present every call is made to it first and then to core: both plugins
 * remember the last style and re-apply their own copy on an Android configuration change
 * (rotation, dark mode) in unspecified order, so they must agree; and on iOS, where the
 * config sets `overlaysWebView: false`, only the package's hide()/show() move the WebView
 * frame and its bar-coloured background view (core only flips the bar's visibility). */
export type BarContrast = "light-content" | "dark-content";
export type SystemBarsApi = {
  setContrast(c: BarContrast): Promise<void>;
  /** Paint the bar itself; a no-op where the platform draws it transparent. */
  setColour(hex: string): Promise<void>;
  hide(): Promise<void>;
  show(): Promise<void>;
};
type StatusBarPlugin = {
  setBackgroundColor: (o: { color: string }) => Promise<void>;
  /** Same inverted naming as core: DARK = light icons on a dark bar. */
  setStyle: (o: { style: "DARK" | "LIGHT" }) => Promise<void>;
  hide: () => Promise<void>;
  show: () => Promise<void>;
};
function systemBars(): SystemBarsApi | null {
  if (!native) return null;
  const statusBar = hasPlugin("StatusBar") ? registerPlugin<StatusBarPlugin>("StatusBar") : null;
  const quiet = async (p: Promise<void>): Promise<void> => {
    try {
      await p;
    } catch {
      /* the platform has no such bar control (iOS colour, hidden bars): nothing to do */
    }
  };
  return {
    // Capacitor's naming is inverted: Dark = light icons on a dark background.
    setContrast: async (c) => {
      const dark = c === "light-content";
      if (statusBar) await quiet(statusBar.setStyle({ style: dark ? "DARK" : "LIGHT" }));
      await quiet(SystemBars.setStyle({ style: dark ? SystemBarsStyle.Dark : SystemBarsStyle.Light }));
    },
    setColour: (hex) => (statusBar ? quiet(statusBar.setBackgroundColor({ color: hex })) : Promise.resolve()),
    hide: async () => {
      if (statusBar) await quiet(statusBar.hide());
      await quiet(SystemBars.hide());
    },
    show: async () => {
      if (statusBar) await quiet(statusBar.show());
      await quiet(SystemBars.show());
    },
  };
}

export type Dictation = {
  /** Start listening; `onText` gets the running transcript; returns a stop function. */
  start: (lang: string, onText: (text: string, final: boolean) => void, onError: (e: string) => void) => () => void;
  /** Where the audio goes, for the UI to say so: the browser vendor's service on the web. */
  processor: "browser-vendor" | "on-device";
};

type SpeechCtor = new () => {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function webDictation(): Dictation | null {
  const w = window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  if (!Ctor) return null;
  return {
    processor: "browser-vendor",
    start: (lang, onText, onError) => {
      const rec = new Ctor();
      rec.lang = lang;
      rec.interimResults = true;
      rec.continuous = true;
      rec.onresult = (e) => {
        let text = "";
        let final = true;
        for (let i = 0; i < e.results.length; i++) {
          const r = e.results[i]!;
          text += r[0]?.transcript ?? "";
          if (!r.isFinal) final = false;
        }
        onText(text.trim(), final);
      };
      rec.onerror = (e) => onError(e.error);
      rec.start();
      return () => rec.stop();
    },
  };
}

/** A second window for the projector route (STORY-071): the desktop shell opens a native
 * window; the web falls back to a browser tab.
 *
 * Pressing the button twice means "bring it back in front of the slides", so the window has to
 * be found again rather than opened again. The shell's `open_projector` already navigates and
 * focuses the one it has; on the web the handle is kept here, because a fresh
 * `window.open(url, "_blank")` would open a second tab every press. Keeping it here and not in
 * the live view is what lets the live view stay free of `window.open`, which the desktop
 * webview does not implement. */
export type ProjectorWindow = {
  native: boolean;
  /** Open it, or focus the one already up. False means nothing opened (a blocked pop-up). */
  open: (sessionId: string, url: string) => Promise<boolean>;
  /** Whether the window we opened is still up. Always true on the native path, which has no
   * way to hear about a close either. */
  isOpen: () => boolean;
};

type TauriGlobal = { core?: { invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown> } };

function projectorWindow(): ProjectorWindow {
  const tauri = (window as unknown as { __TAURI__?: TauriGlobal }).__TAURI__;
  const invoke = tauri?.core?.invoke;
  if (invoke) {
    return {
      native: true,
      isOpen: () => true,
      open: async (sessionId) => {
        try {
          await invoke("open_projector", { sessionId });
          return true;
        } catch {
          // The shell refused (a shell older than the command, or the ACL grant missing):
          // the button must say so rather than claim a window it never opened.
          return false;
        }
      },
    };
  }
  let held: Window | null = null;
  return {
    native: false,
    isOpen: () => !!held && !held.closed,
    open: async (sessionId, url) => {
      if (held && !held.closed) {
        held.focus();
        return true;
      }
      held = window.open(url, `projector-${sessionId}`);
      return !!held;
    },
  };
}

/** One handle on the desktop shell's commands, so the three capabilities below do not each
 * re-derive it. Null everywhere but the Teacher Computer app. */
function desktopInvoke(): ((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null {
  if (typeof window === "undefined") return null;
  return (window as unknown as { __TAURI__?: TauriGlobal }).__TAURI__?.core?.invoke ?? null;
}

/** Print the page (2026-09-11).
 *
 * `window.print()` is dead in the desktop shell: its webview implements no print delegate, so
 * the call returns having done nothing and not even `beforeprint` fires. The two screens that
 * offered it -- the course dashboard and the alignment report -- had a button that did
 * nothing at all, and the teacher's goal there is a PDF to keep or send.
 *
 * The shell therefore runs the platform's own print operation on the window (`print_page`).
 * That was the right answer rather than rendering a PDF ourselves for three reasons: it reads
 * the very page on screen, so the `@media print` rules already in `styles.css` decide the
 * layout and the desktop output matches the browser's instead of forking; on macOS the panel
 * carries "PDF > Save as PDF", which is the keep-or-send the teacher wanted, while still
 * allowing actual paper, which a PDF-only path would have taken away; and it costs a few
 * lines of Rust rather than a print engine inside a product whose rule is that the product is
 * the web app. On the web nothing changes: `window.print()`, as today. */
export type Printing = {
  /** True where the shell prints for us rather than the page printing itself. */
  native: boolean;
  /** Resolves false when nothing could be opened, so a screen can say so. */
  print: () => Promise<boolean>;
};
function printing(): Printing {
  const invoke = desktopInvoke();
  if (!invoke) {
    return {
      native: false,
      print: async () => {
        if (typeof window === "undefined" || typeof window.print !== "function") return false;
        window.print();
        return true;
      },
    };
  }
  return {
    native: true,
    print: async () => {
      try {
        await invoke("print_page");
        return true;
      } catch {
        // A platform whose webview has no print operation: the page's own call may still work.
        try {
          window.print();
          return true;
        } catch {
          return false;
        }
      }
    },
  };
}

/** Open a link outside the app (2026-09-11).
 *
 * `window.open()` returns null in the desktop shell and never navigates, and `<a
 * target="_blank">` is dead for the same reason: the webview library only implements the
 * "create a web view" delegate when the host application supplies a handler, and the shell
 * supplies none. That killed the Telegram link on the profile screen and every reading link.
 *
 * The shell hands the URL to the desktop instead, which is where an external link belongs on a
 * computer. On the web this is exactly today's `window.open`, including the pop-up blocker's
 * verdict: `false` back means the caller must show the link itself. */
export type Opener = {
  /** True where the link leaves for the desktop rather than for a browser tab. */
  native: boolean;
  /** False means the platform refused the URL. A browser cannot tell a blocked pop-up from a
   * successful one -- `noopener` makes `window.open` return null either way -- so on the web
   * this answers true and the caller must show the link beside the button regardless. */
  open: (url: string) => Promise<boolean>;
};
function opener(): Opener {
  const invoke = desktopInvoke();
  if (!invoke) {
    return {
      native: false,
      open: async (url) => {
        // `noopener` is not negotiable for a link that leaves the app: the page it opens must
        // not get a handle back on ours. It is also why there is nothing to return.
        window.open(url, "_blank", "noopener");
        return true;
      },
    };
  }
  return {
    native: true,
    open: async (url) => {
      try {
        await invoke("open_external", { url });
        return true;
      } catch {
        return false;
      }
    },
  };
}

/** Save a file the teacher can find (2026-09-11).
 *
 * A browser answers `<a download>.click()` by writing to the download folder and listing it.
 * The desktop shell answers it by cancelling the navigation: its webview library attaches a
 * download delegate only when the host application supplies a download handler, and with none
 * attached `shouldPerformDownload` navigations are refused outright -- no file, no error, no
 * way for the page to tell. Every export in the app went that way: the QTI zip, both CSVs and
 * a student's own data.
 *
 * On the desktop the shell writes the file to the same place a browser would, the download
 * folder, and answers with the path it used, which is what makes "saved" honest here: the
 * screen names the file and `reveal` opens the folder. The alternative, a save panel, would
 * buy the teacher a choice of folder at the price of a dialog on every export; this keeps the
 * desktop doing what the browser button does. On the web nothing changes at all -- the anchor
 * click, and the browser's own download list as the receipt. */
export type SaveResult =
  /** The browser took it; its own download UI is the receipt. Say nothing extra. */
  | { kind: "browser" }
  | { kind: "saved"; path: string }
  | { kind: "failed"; reason: string };
export type FileSave = {
  /** True where the file is written by the shell and `path` comes back. */
  native: boolean;
  save: (filename: string, blob: Blob) => Promise<SaveResult>;
  /** Show the file's folder; null where the platform has no file manager to show it in. */
  reveal: ((path: string) => Promise<void>) | null;
};
async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  // In chunks: `apply` on a whole multi-megabyte array overflows the argument list.
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}
function fileSave(): FileSave {
  const invoke = desktopInvoke();
  if (!invoke) {
    return {
      native: false,
      reveal: null,
      save: async (filename, blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        return { kind: "browser" };
      },
    };
  }
  return {
    native: true,
    save: async (filename, blob) => {
      try {
        const path = await invoke("save_file", { name: filename, data: await toBase64(blob) });
        return typeof path === "string" ? { kind: "saved", path } : { kind: "failed", reason: "no path" };
      } catch (e) {
        return { kind: "failed", reason: String(e) };
      }
    },
    reveal: async (path) => {
      try {
        await invoke("reveal_file", { path });
      } catch {
        /* the folder went away, or the desktop has no file manager: nothing to show */
      }
    },
  };
}

/** The desktop shell re-issues the local host token (STORY-073): it learned it from the
 * sidecar's stdout and keeps it in the `host_info` command, so the web can recover it when
 * storage was cleared. Null elsewhere. */
function hostToken(): (() => Promise<string | null>) | null {
  const tauri = (window as unknown as { __TAURI__?: TauriGlobal }).__TAURI__;
  const invoke = tauri?.core?.invoke;
  if (!invoke) return null;
  return async () => {
    try {
      const r = await invoke("host_info");
      return (r as { host_token?: string }).host_token || null;
    } catch {
      return null;
    }
  };
}

/** Lesson focus mode (STORY-079): Android only; never claimed on iOS. */
export type FocusStatus = { available: boolean; permitted: boolean; active: boolean };
export type FocusMode = {
  status: () => Promise<FocusStatus>;
  requestPermission: () => Promise<FocusStatus>;
  enter: () => Promise<FocusStatus>;
  exit: () => Promise<FocusStatus>;
};
function focusMode(): FocusMode | null {
  if (platform !== "android" || !hasPlugin("FocusMode")) return null;
  return registerPlugin<FocusMode>("FocusMode");
}

/** Keep the screen awake during a lesson (STORY-080): the Screen Wake Lock API, which
 * WKWebView and Safari support from iOS 16.4; null where the API is missing. */
export type WakeLock = { acquire: () => Promise<boolean>; release: () => Promise<void> };
function wakeLock(): WakeLock | null {
  const nav = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } };
  if (!nav.wakeLock) return null;
  let sentinel: { release: () => Promise<void> } | null = null;
  return {
    acquire: async () => {
      try {
        sentinel = await nav.wakeLock!.request("screen");
        return true;
      } catch {
        return false;
      }
    },
    release: async () => {
      try {
        await sentinel?.release();
      } finally {
        sentinel = null;
      }
    },
  };
}

/** NFC tags (STORY-082): Web NFC where the browser has it (Android Chrome and the Android
 * shells); iOS has no Web NFC, so the join code and QR stay the primary path. */
export type Nfc = { read: () => Promise<string | null>; write: (url: string) => Promise<boolean> };
type NdefRecord = { recordType: string; data?: DataView; encoding?: string };
type NdefReaderCtor = new () => {
  scan: (o?: { signal?: AbortSignal }) => Promise<void>;
  write: (m: { records: { recordType: string; data: string }[] }) => Promise<void>;
  onreading: ((e: { message: { records: NdefRecord[] } }) => void) | null;
  onreadingerror: (() => void) | null;
};
function nfc(): Nfc | null {
  const ctor = (window as unknown as { NDEFReader?: NdefReaderCtor }).NDEFReader;
  if (!ctor) return null;
  return {
    read: () =>
      new Promise((resolve) => {
        const reader = new ctor();
        const ctl = new AbortController();
        const done = (v: string | null) => { ctl.abort(); resolve(v); };
        const timer = window.setTimeout(() => done(null), 30_000);
        reader.onreadingerror = () => { window.clearTimeout(timer); done(null); };
        reader.onreading = (e) => {
          window.clearTimeout(timer);
          for (const r of e.message.records) {
            if ((r.recordType === "url" || r.recordType === "text") && r.data) {
              done(new TextDecoder(r.encoding || "utf-8").decode(r.data));
              return;
            }
          }
          done(null);
        };
        reader.scan({ signal: ctl.signal }).catch(() => { window.clearTimeout(timer); done(null); });
      }),
    write: async (url) => {
      try {
        await new ctor().write({ records: [{ recordType: "url", data: url }] });
        return true;
      } catch {
        return false;
      }
    },
  };
}

/** Join by camera (STORY-078): the official barcode scanner when the shell ships it. */
export type CodeScanner = { scan: () => Promise<string | null> };
type BarcodePlugin = { scanBarcode: (o: { hint: number }) => Promise<{ ScanResult: string }> };
/** STORY-111: the device's own addresses, so a room scan targets the right subnet. */
export type LanApi = { addresses(): Promise<{ addresses: { ip: string; prefix: number }[] }> };
function lan(): LanApi | null {
  if (!native || !hasPlugin("Lan")) return null;
  return registerPlugin<LanApi>("Lan");
}

function codeScanner(): CodeScanner | null {
  if (!hasPlugin("CapacitorBarcodeScanner")) return null;
  const plugin = registerPlugin<BarcodePlugin>("CapacitorBarcodeScanner");
  return {
    scan: async () => {
      try {
        const r = await plugin.scanBarcode({ hint: 0 }); // 0 = QR code
        return r.ScanResult || null;
      } catch {
        return null;
      }
    },
  };
}

/** Biometric lock (STORY-078): a local prompt, nothing leaves the device. */
export type Biometric = { available: () => Promise<boolean>; authenticate: (reason: string) => Promise<boolean> };
type BioPlugin = {
  checkBiometry: () => Promise<{ isAvailable: boolean }>;
  authenticate: (o: { reason: string }) => Promise<void>;
};
function biometric(): Biometric | null {
  if (!hasPlugin("BiometricAuthNative")) return null;
  const plugin = registerPlugin<BioPlugin>("BiometricAuthNative");
  return {
    available: async () => (await plugin.checkBiometry()).isAvailable,
    authenticate: async (reason) => {
      try {
        await plugin.authenticate({ reason });
        return true;
      } catch {
        return false;
      }
    },
  };
}

/** Push registration (STORY-078): FCM/APNs token kept locally until STORY-075 registers it. */
export type Push = { register: () => Promise<string | null> };
type PushPlugin = {
  requestPermissions: () => Promise<{ receive: string }>;
  register: () => Promise<void>;
  addListener: (event: string, cb: (t: { value: string }) => void) => Promise<unknown>;
};
function push(): Push | null {
  if (!hasPlugin("PushNotifications")) return null;
  const plugin = registerPlugin<PushPlugin>("PushNotifications");
  return {
    register: () =>
      new Promise<string | null>((resolve) => {
        void (async () => {
          const perm = await plugin.requestPermissions();
          if (perm.receive !== "granted") return resolve(null);
          const timer = window.setTimeout(() => resolve(null), 15_000);
          await plugin.addListener("registration", (t) => {
            window.clearTimeout(timer);
            localStorage.setItem(nsKey("push_token"), t.value);
            resolve(t.value);
          });
          await plugin.register();
        })();
      }),
  };
}

export type Capabilities = {
  platform: Platform;
  dictation: Dictation | null;
  projectorWindow: ProjectorWindow;
  /** Print the page; the browser's own `window.print` is dead in the desktop shell. */
  printing: Printing;
  /** Open a link outside the app; `window.open` is dead in the desktop shell. */
  opener: Opener;
  /** Write a file the teacher can find; `<a download>` is dead in the desktop shell. */
  fileSave: FileSave;
  /** Running inside the desktop shell (the local host serves us). */
  desktop: boolean;
  /** The desktop shell re-issues the local host token; null elsewhere. */
  hostToken: (() => Promise<string | null>) | null;
  /** Which Capacitor shell bundles us; null on the web and the desktop. */
  shell: Shell;
  /** Status and navigation bars on the native shells; null on the web. */
  systemBars: SystemBarsApi | null;
  focusMode: FocusMode | null;
  wakeLock: WakeLock | null;
  nfc: Nfc | null;
  codeScanner: CodeScanner | null;
  /** A photo for the card scan (STORY-089): a browser's file input where the pointer is coarse
   * (a phone or tablet); null in every shell and on a laptop. */
  cardCamera: CardCamera | null;
  lan: LanApi | null;
  biometric: Biometric | null;
  push: Push | null;
};

export const capabilities: Capabilities = {
  platform,
  dictation: typeof window === "undefined" ? null : webDictation(),
  projectorWindow:
    typeof window === "undefined" ? { native: false, open: async () => false, isOpen: () => false } : projectorWindow(),
  printing: printing(),
  opener: typeof window === "undefined" ? { native: false, open: async () => false } : opener(),
  fileSave:
    typeof window === "undefined"
      ? { native: false, reveal: null, save: async () => ({ kind: "failed", reason: "no window" }) }
      : fileSave(),
  desktop: typeof window !== "undefined" && !!(window as unknown as { __TAURI__?: unknown }).__TAURI__,
  hostToken: typeof window === "undefined" ? null : hostToken(),
  shell,
  systemBars: typeof window === "undefined" ? null : systemBars(),
  focusMode: typeof window === "undefined" ? null : focusMode(),
  wakeLock: typeof window === "undefined" ? null : wakeLock(),
  nfc: typeof window === "undefined" ? null : nfc(),
  codeScanner: typeof window === "undefined" ? null : codeScanner(),
  cardCamera:
    typeof window === "undefined"
      ? null
      : cardCameraFor({ desktop: !!(window as unknown as { __TAURI__?: unknown }).__TAURI__, platform, coarse: coarsePointer() }),
  lan: typeof window === "undefined" ? null : lan(),
  biometric: typeof window === "undefined" ? null : biometric(),
  push: typeof window === "undefined" ? null : push(),
};
